import { AxiosError } from 'axios'
import { ApplicationPaths, authService } from 'partner-oidc-auth'
import type { IApiContext } from './type.api-alert'

export const ApiAlertContext = (globalThis as any).ApiAlertContext as IApiContext | undefined

if (process.env.NODE_ENV === 'development') {
  ;(globalThis as any).authService = authService
}

export const sleep = (sec: number) => new Promise((res) => setTimeout(res, sec))

export const fetchDelay = async function <TModel>(action: () => Promise<TModel>, sec: number) {
  const [res] = await Promise.all([action(), sleep(sec)])
  return res
}

/**
 * Resolves a base URL string into a full base URL.
 * - If `baseUri` is already a full URL, it is returned as-is.
 * - Otherwise it is prefixed with `REACT_APP_ADMIN_API` or `window.location.origin`.
 *
 * This lets callers pass either a full URL (e.g. `https://api.example.com/v1`) or
 * a path suffix (e.g. `/api/v1`) and get a proper base URL for the HTTP client.
 */
export const resolveBaseUrl = (baseUri: string) => {
  const prefix = process.env.APP_API_URL || window.location.origin || ''
  if (!baseUri) return prefix
  try {
    // new URL will succeed for absolute URLs like "https://..." or "http://...".
    // If it parses, treat it as a full URL and return as-is.
    new URL(baseUri)
    return baseUri
  } catch {
    // For relative paths, prefix with configured API base or current origin.
    return `${prefix}${baseUri}`
  }
}

interface ErrorModel {
  [key: string]: string[]
}

/**
 * Processes axios errors and displays error messages to the user
 * @param err - The axios error containing response data
 */
export const processError = (err: AxiosError<ErrorModel>) => {
  const errorMessages = Object.values(err.response?.data?.errors ?? err.response?.data ?? {})
    .filter((x) => x.length > 0)
    .map((x) => x[0])
  if (errorMessages.length < 1) return
  ApiAlertContext?.ApiAlert?.PushError(errorMessages.join('\n'))
}

/**
 * Checks if the current user's token is expired.
 * @returns {Promise<boolean>} True if token is expired or user not logged in, false otherwise.
 */
export const checkTokenExpiry = async () => {
  try {
    const user = await authService.getUser()
    if (!user) {
      console.log('No user is currently logged in.')
      return true
    }

    const now = Math.floor(Date.now() / 1000) // Current time in seconds since epoch
    const expiresIn = user.exp - now

    if (expiresIn <= 0) {
      console.log('The token has expired.')
      return true
    } else {
      console.log(`The token will expire in ${expiresIn} seconds.`)
      return false
    }
  } catch (error) {
    console.error('Error checking token expiry:', error)
    return true
  }
}
/**
 * Refreshes the token once if at least 4 seconds have passed since the last refresh.
 * Uses localStorage key 'ltt' to track last refresh time.
 */
export const refreshTokenOnce = async () => {
  const timeStamp = new Date().getTime() - parseInt(localStorage.getItem('ltt') ?? '0')
  localStorage.setItem('ltt', new Date().getTime().toString())
  if (timeStamp >= 4000) {
    try {
      await authService.ensureUserManagerInitialized()
      await authService.signIn({})
    } catch (e) {
      console.log(e)
    }
  }
}
/**
 * Handles errors from Axios HTTP responses.
 * Redirects or signs out user based on error code and status.
 * @param {AxiosError} err - The error object from Axios.
 * @returns {Promise<never>} Always rejects with the error.
 */
export const handleResponseError = async (err: AxiosError) => {
  if (err.response?.status === 403) {
    const dataError: { Code: number; Message: string } = err.response.data as any
    if (typeof dataError?.Code !== 'number') return Promise.reject(err)
    switch (dataError.Code) {
      case 3: {
        await authService.signOut({ returnUrl: authService.getReturnUrl() })
        break
      }
      case 1: {
        await authService.userManager?.revokeTokens(['access_token'])
        window.location.replace(ApplicationPaths.Origin(''))
        break
      }
      default: {
        window.location.replace(ApplicationPaths.Origin(ApplicationPaths.IdentityAccessDenied))
        break
      }
    }
  } else if (err.response?.status === 401) {
  } else if (err.code === 'ERR_CANCELED') {
  } else if ((err.response?.status ?? 500) >= 500) {
    if (err.code !== 'ERR_CANCELED') {
      ApiAlertContext?.ApiAlert?.PushError('An error has occurred!')
    }
  } else if (err.response?.status === 404) {
    ApiAlertContext?.ApiAlert?.PushError('Resource not found!')
  }
  return Promise.reject(err)
}
